import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { getFeaturedPost } from '../config/featuredBlogPosts';
import { mediaUrl, formatBlogDate } from '../utils/blogUtils';
import BlogShareBar from './BlogShareBar';
import { renderWellnessSection } from './WellnessGuideBlogArticle';
import {
  TournamentRecapAwards,
  TournamentRecapPhotosLoading,
  TournamentRecapSquad,
  TournamentRecapTeamPhotos,
} from './TournamentRecapPhotoSections';
import { useBlogPostMeta } from '../hooks/useBlogPostMeta';
import './WellnessGuideBlogArticle.css';
import './NamslBaltimoreTuskersRecapArticle.css';

const TournamentRecapBlogArticle = () => {
  const { slug } = useParams();
  const post = getFeaturedPost(slug);
  const [tournament, setTournament] = useState(null);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [photosError, setPhotosError] = useState('');

  useBlogPostMeta(post);

  const heroBackground = useMemo(() => {
    const teamPhoto = tournament?.teamPhotoUrls?.[0];
    return teamPhoto ? mediaUrl(teamPhoto) : post?.coverImageUrl;
  }, [tournament, post?.coverImageUrl]);

  const loadTournament = useCallback(async () => {
    if (!post?.tournamentShareToken) {
      setPhotosLoading(false);
      return;
    }
    try {
      setPhotosLoading(true);
      setPhotosError('');
      const data = await api.getPublicTournament(post.tournamentShareToken);
      setTournament(data);
    } catch (err) {
      setTournament(null);
      setPhotosError(err.message || 'Could not load tournament photos');
    } finally {
      setPhotosLoading(false);
    }
  }, [post?.tournamentShareToken]);

  useEffect(() => {
    loadTournament();
  }, [loadTournament]);

  if (!post || post.component !== 'TournamentRecapBlogArticle') {
    return (
      <div className="blog-page">
        <p className="blog-page__status">Post not found</p>
      </div>
    );
  }

  const hasPhotoContent = Boolean(
    tournament?.teamPhotoUrls?.length
    || tournament?.memberPhotos?.length
    || tournament?.awards?.some((a) => a.playerName || a.photoUrl)
  );

  const renderSections = () => {
    const elements = [];

    post.sections.forEach((section, index) => {
      elements.push(renderWellnessSection(section, index));

      if (section.type === 'stats') {
        if (photosLoading) {
          elements.push(<TournamentRecapPhotosLoading key="photos-loading" />);
        } else if (!photosError) {
          elements.push(
            <TournamentRecapTeamPhotos
              key="team-photos"
              photos={tournament?.teamPhotoUrls}
              copy={post.photoSections?.teamPhotos}
            />
          );
        }
      }

      if (section.title === 'Top scorers & assists' && !photosLoading && !photosError) {
        elements.push(
          <TournamentRecapSquad
            key="squad"
            members={tournament?.memberPhotos}
            playerStats={tournament?.playerStats}
            copy={post.photoSections?.squad}
          />
        );
        elements.push(
          <TournamentRecapAwards
            key="awards"
            awards={tournament?.awards}
            copy={post.photoSections?.awards}
          />
        );
      }
    });

    if (!photosLoading && photosError) {
      elements.push(
        <section key="photos-error" className="wellness-article__panel tuskers-recap__photos-error">
          <p>{photosError}</p>
          <button type="button" className="btn btn-secondary" onClick={loadTournament}>
            Retry loading photos
          </button>
        </section>
      );
    }

    if (!photosLoading && !photosError && !hasPhotoContent) {
      elements.push(
        <section key="photos-empty" className="wellness-article__panel tuskers-recap__photos-error">
          <p>Tournament photos and awards have not been uploaded yet. Check back after they are added in Manage Tournament.</p>
        </section>
      );
    }

    return elements;
  };

  return (
    <article className="tuskers-recap wellness-article wellness-article--club">
      <header className="tuskers-recap__hero">
        <div
          className="tuskers-recap__hero-bg"
          style={{ backgroundImage: `url(${heroBackground})` }}
          aria-hidden="true"
        />
        <div className="tuskers-recap__hero-scrim" aria-hidden="true" />
        <div className="tuskers-recap__hero-content">
          <Link to="/blog" className="tuskers-recap__back">
            ← Back to blog
          </Link>
          <div className="tuskers-recap__hero-top">
            {tournament?.clubLogoUrl && (
              <img
                src={mediaUrl(tournament.clubLogoUrl)}
                alt=""
                className="tuskers-recap__logo"
              />
            )}
            <div>
              <p className="tuskers-recap__kicker">
                <span className="tuskers-recap__badge">Tournament recap</span>
                <span>{formatBlogDate(post.publishedAt)}</span>
                <span>By {post.authorName}</span>
                {post.clubName && <span>{post.clubName}</span>}
              </p>
              <h1>{post.title}</h1>
              <p className="tuskers-recap__excerpt">{post.excerpt}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="tuskers-recap__shell wellness-article__shell">
        {renderSections()}

        {post.relatedPosts?.length > 0 && (
          <section className="wellness-article__panel wellness-article__related">
            <h2>{post.relatedPostsTitle || 'Related articles'}</h2>
            <ul>
              {post.relatedPosts.map((related) => (
                <li key={related.slug}>
                  <Link to={`/blog/${related.slug}`}>{related.title}</Link>
                  {related.blurb ? ` — ${related.blurb}` : ''}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="wellness-article__panel wellness-article__share">
          <BlogShareBar post={post} />
        </section>

        {post.referenceLinks?.length > 0 && (
          <section className="wellness-article__panel">
            <h2>Related links</h2>
            <ul>
              {post.referenceLinks.map((link) => (
                <li key={link.url}>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </article>
  );
};

export default TournamentRecapBlogArticle;
